import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, DeviceMobile, Copy, DownloadSimple, CheckCircle, Key, WarningCircle,
} from "@phosphor-icons/react";

// ---- Backup codes display (shown once) --------------------------------------
export function BackupCodesView({ codes, onDone, title = "Save your backup codes" }) {
  const text = (codes || []).join("\n");
  const copy = () => { navigator.clipboard?.writeText(text); toast.success("Backup codes copied"); };
  const download = () => {
    const blob = new Blob([`WorkWithLottie — two-factor backup codes\nKeep these safe. Each code works once.\n\n${text}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "workwithlottie-backup-codes.txt"; a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div data-testid="twofa-backup-codes">
      <div className="flex items-center gap-2 overline text-orangeink mb-1"><Key size={16} weight="fill" /> Recovery</div>
      <h3 className="font-heading font-bold text-xl text-navy mb-1">{title}</h3>
      <p className="text-navy/70 text-sm mb-4">
        Store these somewhere safe. If you lose access to your authenticator app, each code lets you sign in once.
      </p>
      <div className="grid grid-cols-2 gap-2 bg-navy/[0.03] border border-slate-200 rounded-xl p-4 font-mono text-sm text-navy mb-4">
        {(codes || []).map((c) => <div key={c} className="tracking-wider">{c}</div>)}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={copy} data-testid="twofa-backup-copy"
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors"><Copy size={15} /> Copy</button>
        <button onClick={download} data-testid="twofa-backup-download"
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors"><DownloadSimple size={15} /> Download</button>
        <button onClick={onDone} data-testid="twofa-backup-done"
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors ml-auto">
          I've saved them
        </button>
      </div>
    </div>
  );
}

// ---- Enrolment (QR + verify). Works with a login challenge OR authenticated --
export function EnrolTOTP({ challengeToken, rememberDevice = false, onEnrolled, compact = false }) {
  const [setup, setSetup] = useState(null); // {secret, qr, otpauth_uri}
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.post("/auth/2fa/setup", challengeToken ? { challenge_token: challengeToken } : {})
      .then(({ data }) => setSetup(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [challengeToken]);

  const enable = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", {
        code: code.trim(),
        ...(challengeToken ? { challenge_token: challengeToken, remember_device: rememberDevice } : {}),
      });
      onEnrolled(data); // {backup_codes, [user, access_token]}
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="twofa-enrol">
      {!compact && (
        <>
          <div className="flex items-center gap-2 overline text-orangeink mb-1"><ShieldCheck size={16} weight="fill" /> Two-factor</div>
          <h3 className="font-heading font-bold text-xl text-navy mb-1">Set up your authenticator</h3>
          <p className="text-navy/70 text-sm mb-4">
            Scan the QR code with Google Authenticator, Authy or 1Password, then enter the 6-digit code to confirm.
          </p>
        </>
      )}
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <div className="shrink-0">
          {setup?.qr
            ? <img src={setup.qr} alt="Authenticator QR code" data-testid="twofa-setup-qr" className="w-40 h-40 rounded-xl border border-slate-200" />
            : <div className="w-40 h-40 rounded-xl border border-slate-200 flex items-center justify-center text-navy/65 text-xs">Loading…</div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-navy/70 text-xs mb-1">Can't scan? Enter this key manually:</div>
          <div data-testid="twofa-setup-secret" className="font-mono text-sm text-navy break-all bg-navy/[0.03] border border-slate-200 rounded-lg px-3 py-2 mb-4">
            {setup?.secret || "…"}
          </div>
          <form onSubmit={enable} className="space-y-3">
            <input data-testid="twofa-setup-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="w-full sm:w-48 text-center tracking-[0.5em] font-mono text-lg bg-white border border-slate-200 rounded-lg px-3 py-2.5 outline-none focus:border-[#F37A24]"
              placeholder="000000" />
            {error && <div data-testid="twofa-enrol-error" className="text-negative text-sm font-medium">{error}</div>}
            <button type="submit" disabled={loading || code.length !== 6} data-testid="twofa-setup-verify"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors disabled:opacity-50">
              {loading ? "Verifying…" : "Verify & enable"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---- Verify step at login ---------------------------------------------------
export function VerifyTOTP({ challengeToken, onVerified }) {
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", {
        challenge_token: challengeToken, code: code.trim(), remember_device: remember,
      });
      onVerified(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="twofa-verify">
      <div className="flex items-center gap-2 overline mb-2" style={{ color: "#F37A24" }}>
        <DeviceMobile size={16} weight="fill" /> Two-step verification
      </div>
      <h2 className="font-heading font-bold text-3xl tracking-tighter text-navy mb-2">Enter your code</h2>
      <p className="text-navy/70 text-sm mb-6">
        {useBackup ? "Enter one of your backup recovery codes." : "Open your authenticator app and enter the 6-digit code."}
      </p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="twofa-verify-code" autoFocus autoComplete="one-time-code"
          value={code} onChange={(e) => setCode(useBackup ? e.target.value : e.target.value.replace(/\D/g, ""))}
          maxLength={useBackup ? 9 : 6}
          className="w-full text-center tracking-[0.4em] font-mono text-xl bg-white border border-slate-200 rounded-xl px-3 py-3 outline-none focus:border-[#F37A24]"
          placeholder={useBackup ? "xxxx-xxxx" : "000000"} />
        <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer select-none">
          <input type="checkbox" data-testid="twofa-remember-device" checked={remember}
            onChange={(e) => setRemember(e.target.checked)} className="accent-[#F37A24] w-4 h-4" />
          Trust this device for 30 days
        </label>
        {error && <div data-testid="twofa-verify-error" className="text-negative text-sm font-medium">{error}</div>}
        <button type="submit" disabled={loading || !code.trim()} data-testid="twofa-verify-submit"
          className="w-full text-white font-semibold py-3 rounded-xl transition-colors bg-[#F37A24] hover:bg-navy disabled:opacity-50">
          {loading ? "VERIFYING…" : "Verify & sign in"}
        </button>
      </form>
      <button onClick={() => { setUseBackup(!useBackup); setCode(""); setError(""); }} data-testid="twofa-use-backup"
        className="mt-4 text-sm font-semibold text-navy/70 hover:text-navy">
        {useBackup ? "← Use authenticator code instead" : "Use a backup code instead"}
      </button>
    </div>
  );
}

// ---- Login-time 2FA orchestrator (verify or enforced setup) -----------------
export function LoginTwoFactor({ challenge, onAuthed }) {
  const [backupCodes, setBackupCodes] = useState(null);
  const [remember] = useState(false);
  const [authPayload, setAuthPayload] = useState(null);

  if (backupCodes) {
    return (
      <div className="card-surface p-6">
        <BackupCodesView codes={backupCodes} onDone={() => onAuthed(authPayload)} />
      </div>
    );
  }
  if (challenge.two_factor === "setup") {
    return (
      <div>
        <div className="flex items-center gap-2 overline mb-2" style={{ color: "#F37A24" }}>
          <ShieldCheck size={16} weight="fill" /> Security required
        </div>
        <h2 className="font-heading font-bold text-3xl tracking-tighter text-navy mb-2">Set up two-factor</h2>
        <p className="text-navy/70 text-sm mb-6">Your account requires two-factor authentication. Set it up now to continue.</p>
        <EnrolTOTP challengeToken={challenge.challenge_token} rememberDevice={remember} compact
          onEnrolled={(data) => { setAuthPayload({ user: data.user, access_token: data.access_token }); setBackupCodes(data.backup_codes); }} />
      </div>
    );
  }
  return <VerifyTOTP challengeToken={challenge.challenge_token} onVerified={onAuthed} />;
}

// ---- Security settings: manage own 2FA (opt-in / disable / regenerate) ------
export function TwoFactorSettings() {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("idle"); // idle | enrol | codes | disable | regen
  const [codes, setCodes] = useState(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/2fa/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: confirmCode.trim() });
      toast.success("Two-factor disabled");
      setMode("idle"); setConfirmCode(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const regen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/regenerate", { code: confirmCode.trim() });
      setCodes(data.backup_codes); setMode("codes"); setConfirmCode(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <div className="card-surface p-5 max-w-2xl" data-testid="twofa-settings">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-navy font-semibold mb-1 flex items-center gap-2">
            <ShieldCheck size={18} weight="fill" className={status.enabled ? "text-positive" : "text-navy/65"} />
            Two-factor authentication
          </h3>
          <p className="text-navy/70 text-sm max-w-md">
            {status.enabled
              ? "Your account is protected with an authenticator app."
              : "Add an extra layer of security using an authenticator app."}
          </p>
          <div className="mt-2 text-xs" data-testid="twofa-status">
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-semibold ${status.enabled ? "bg-positive/10 text-positive" : "bg-navy/5 text-navy/70"}`}>
              {status.enabled ? <><CheckCircle size={12} weight="fill" /> Enabled</> : "Not enabled"}
            </span>
            {status.enabled && <span className="ml-2 text-navy/65">{status.backup_codes_remaining} backup codes left</span>}
            {status.account_required && <span className="ml-2 text-[#C2410C] font-semibold">Required for your account</span>}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {!status.enabled && mode === "idle" && (
            <button onClick={() => setMode("enrol")} data-testid="twofa-enable-btn"
              className="px-4 py-2.5 text-sm font-semibold rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors">Enable 2FA</button>
          )}
          {status.enabled && mode === "idle" && (
            <>
              <button onClick={() => setMode("regen")} data-testid="twofa-regen-btn"
                className="px-4 py-2.5 text-sm font-semibold rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Regenerate backup codes</button>
              {!status.account_required && (
                <button onClick={() => setMode("disable")} data-testid="twofa-disable-btn"
                  className="px-4 py-2.5 text-sm font-semibold rounded-lg border border-negative/40 text-negative hover:bg-negative hover:text-white transition-colors">Turn off 2FA</button>
              )}
            </>
          )}
        </div>
      </div>

      {mode === "enrol" && (
        <div className="mt-6 pt-6 border-t border-slate-100">
          <EnrolTOTP compact onEnrolled={(data) => { setCodes(data.backup_codes); setMode("codes"); }} />
        </div>
      )}
      {mode === "codes" && (
        <div className="mt-6 pt-6 border-t border-slate-100">
          <BackupCodesView codes={codes} onDone={() => { setMode("idle"); setCodes(null); load(); }} />
        </div>
      )}
      {(mode === "disable" || mode === "regen") && (
        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="flex items-start gap-2 text-navy/70 text-sm mb-3">
            <WarningCircle size={18} className="text-[#C2410C] shrink-0 mt-0.5" />
            Enter a current 6-digit code from your authenticator to confirm.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input data-testid="twofa-confirm-code" inputMode="numeric" maxLength={6} value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ""))}
              className="w-40 text-center tracking-[0.4em] font-mono bg-white border border-slate-200 rounded-lg px-3 py-2.5 outline-none focus:border-[#F37A24]" placeholder="000000" />
            <button onClick={mode === "disable" ? disable : regen} disabled={busy || confirmCode.length !== 6}
              data-testid="twofa-confirm-btn"
              className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-navy text-white hover:bg-orange transition-colors disabled:opacity-50">
              {busy ? "Working…" : mode === "disable" ? "Turn off 2FA" : "Regenerate"}
            </button>
            <button onClick={() => { setMode("idle"); setConfirmCode(""); }}
              className="px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
